import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import type { SocialPostResult, SocialPostFormData, DailyContentResult, DailyContentFormData, DailyPostItem } from '@/pages/social-post/types';

export interface SocialPostGeneration {
  id: string;
  user_id: string;
  generation_type: 'social_post' | 'daily_content' | null;
  business_name: string | null;
  platform: string | null;
  post_type: string | null;
  goal: string | null;
  offer_topic: string | null;
  target_audience: string | null;
  brand_voice: string | null;
  caption_length: string | null;
  include_image: boolean;
  plan_quantity: string | null;
  content_personality: string | null;
  audience_focus: string | null;
  location_context: string | null;
  content_themes: string[] | null;
  daily_posts: DailyPostItem[] | null;
  caption1: string | null;
  caption2: string | null;
  hooks: string[] | null;
  cta_suggestions: string[] | null;
  hashtag_suggestions: string[] | null;
  visual_direction: string | null;
  image_concept: string | null;
  image_url: string | null;
  created_at: string;
}

export function historyItemToResult(item: SocialPostGeneration): SocialPostResult {
  return {
    type: 'social_post',
    caption1: item.caption1 ?? '',
    caption2: item.caption2 ?? '',
    hooks: item.hooks ?? [],
    primaryHook: item.hooks?.[0] ?? '',
    contentAngle: '',
    recommendedCta: item.cta_suggestions?.[0] ?? '',
    ctaSuggestions: item.cta_suggestions ?? [],
    hashtagSuggestions: item.hashtag_suggestions ?? [],
    visualDirection: item.visual_direction ?? '',
    imageConcept: item.image_concept ?? '',
    shortVideoIdea: '',
    platformVariations: {},
    imageUrl: item.image_url ?? null,
    platform: item.platform ?? '',
    postType: item.post_type ?? '',
    businessName: item.business_name ?? '',
  };
}

export function historyItemToDailyResult(item: SocialPostGeneration): DailyContentResult {
  return {
    type: 'daily_content',
    quantity: (item.plan_quantity as DailyContentResult['quantity']) ?? '1',
    platform: item.platform ?? '',
    businessName: item.business_name ?? '',
    posts: Array.isArray(item.daily_posts) ? item.daily_posts : [],
  };
}

export function useSocialPostHistory(limit = 30) {
  const { user } = useAuth();
  const [history, setHistory] = useState<SocialPostGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchHistory = useCallback(async () => {
    if (!user) {
      setHistory([]);
      setLoading(false);
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    const { data, error: err } = await supabase
      .from('social_post_generations')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (err) {
      setError(err.message);
    } else {
      setHistory(data ?? []);
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [user, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`social_post_generations_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_post_generations',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchHistory();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchHistory]);

  const savePost = useCallback(
    async (
      formData: SocialPostFormData | DailyContentFormData,
      result: SocialPostResult | DailyContentResult
    ): Promise<void> => {
      if (!user) return;
      const isDaily = result.type === 'daily_content';
      const payload = isDaily
        ? {
            user_id: user.id,
            generation_type: 'daily_content',
            business_name: formData.businessName || null,
            platform: formData.platform || null,
            post_type: 'Daily Content Plan',
            goal: 'dailyGoal' in formData ? formData.dailyGoal || null : null,
            offer_topic: null,
            target_audience: 'audienceFocus' in formData ? formData.audienceFocus || null : null,
            brand_voice: null,
            caption_length: null,
            include_image: false,
            plan_quantity: result.quantity,
            content_personality: 'contentPersonality' in formData ? formData.contentPersonality || null : null,
            audience_focus: 'audienceFocus' in formData ? formData.audienceFocus || null : null,
            location_context: 'location' in formData ? formData.location || null : null,
            content_themes: 'contentThemes' in formData && formData.contentThemes.length > 0 ? formData.contentThemes : null,
            daily_posts: result.posts.length > 0 ? result.posts : null,
            caption1: result.posts[0]?.caption || null,
            caption2: result.posts[1]?.caption || null,
            hooks: result.posts.map((post) => post.hook).filter(Boolean),
            cta_suggestions: result.posts.map((post) => post.cta).filter(Boolean),
            hashtag_suggestions: result.posts.flatMap((post) => post.hashtags ?? []).filter(Boolean),
            visual_direction: result.posts[0]?.imageDirection || null,
            image_concept: null,
            image_url: null,
          }
        : {
            user_id: user.id,
            generation_type: 'social_post',
            business_name: formData.businessName || null,
            platform: formData.platform || null,
            post_type: 'postType' in formData ? formData.postType || null : null,
            goal: 'goal' in formData ? formData.goal || null : null,
            offer_topic: 'offerTopic' in formData ? formData.offerTopic || null : null,
            target_audience: 'targetAudience' in formData ? formData.targetAudience || null : null,
            brand_voice: 'brandVoice' in formData ? formData.brandVoice || null : null,
            caption_length: 'captionLength' in formData ? formData.captionLength || null : null,
            include_image: 'includeImage' in formData ? formData.includeImage : false,
            caption1: result.caption1 || null,
            caption2: result.caption2 || null,
            hooks: result.hooks.length > 0 ? result.hooks : null,
            cta_suggestions: result.ctaSuggestions.length > 0 ? result.ctaSuggestions : null,
            hashtag_suggestions: result.hashtagSuggestions.length > 0 ? result.hashtagSuggestions : null,
            visual_direction: result.visualDirection || null,
            image_concept: result.imageConcept || null,
            image_url: result.imageUrl || null,
          };

      const { error: insertErr } = await supabase.from('social_post_generations').insert(payload);
      if (insertErr) {
        const migrationHint =
          isDaily &&
          /generation_type|daily_posts|plan_quantity|content_personality|audience_focus|location_context|content_themes/i.test(
            insertErr.message
          );

        const message = migrationHint
          ? 'Daily content history is not enabled in the database yet. Apply the latest Supabase migration and try again.'
          : insertErr.message;

        setError(message);
        throw new Error(message);
      }
    },
    [user]
  );

  const deletePost = useCallback(
    async (id: string): Promise<void> => {
      setHistory((prev) => prev.filter((item) => item.id !== id));
      const { error: delErr } = await supabase
        .from('social_post_generations')
        .delete()
        .eq('id', id);
      if (delErr) {
        setError(delErr.message);
        fetchHistory();
      }
    },
    [fetchHistory]
  );

  return { history, loading, error, refetch: fetchHistory, savePost, deletePost };
}
